import React, { useState, useEffect } from "react";

import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";

import _ from "lodash";
import { toast } from "react-toastify";
import { Pagination } from "@mui/material";

import "./index.css";
import "../property-listing/styles.css";

import Footer from "../../components/footer/Footer";

import notAvailableImg from "../../assets/not_available.png";
import CheckInCheckOutCalender from "../../components/global/checkin-checkout-calander";
import GuestInput from "../../components/guest-input/Guestinput.jsx";

import FiltersDesktop from "../../components/global/filter/FiltersDesktop";

import SkeletonCardsWrapper from "../../components/property-listing-cards/sketon-card";
import Button from "../../components/global/button";
import LocationSearch from "../../components/Locationsearch";
import { filterItems } from "../../components/global/filter/initialStates";
import DestinationListingCard from "../../components/destination-listing/DestinationListingCard";

import PropertyFilterModal from "../../components/property-listing-cards/property-filter-modal";

import { Helmet } from "react-helmet";
import { initialExtraDetailsState, initialSearchState, isSearchValuesChanged } from "../property-listing";
import { generateListingUrl } from "../../utils/formatting";
import moment from "moment";
import { getPropertiesByDestinationApi } from "../../api/propertyApi";
import GlobalImage from "../../components/global/image/image";
import GoogleMap from "../../components/destination-listing/google-map-destination";
import { destinationFilterItems } from "../../components/global/filter/destinationInitialStates";
import DestinationsPropertiesSort from "../../components/destination-listing/property-destinations-sort";
import { generateDestinationListingUrl, processDestinationListingParams } from "../../utils/formatting/distinations-listing/url.utils";
import DestinationListingSortModal from "../../components/destination-listing/destination-listing-sort-modal";

/** Main Component Function*/
function DestinationListing() {
  const [searchCriteria, setSearchCriteria] = useState({
    ...initialSearchState,
    bookingDates: {
      checkinDate: null,
      checkoutDate: null,
    },
  });

  const [errors, setErrors] = useState({
    location: null,
    guests: null,
    bookingDates: null,
  });

  const [properties, setProperties] = useState({});
  const [extraDetails, setExtraDetails] = useState(initialExtraDetailsState);

  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();

  const [searchParams] = useSearchParams();

  const [isTouched, setIsTouched] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [locationDetails, setLocationDetails] = useState({});

  const API_KEY = process.env.REACT_APP_GOOGLE_CITY_API_KEY;
  const selectedLocation = params.destination;

  const url = `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(selectedLocation)}&key=${API_KEY}`;

  useEffect(() => {
    if (!selectedLocation) return;
    fetch(url)
      .then((res) => res.json())
      .then((data) => {
        if (!data.results || !data.results[0]) return;
        const result = data.results[0];

        const { lat, lng } = result.geometry.location;

        let city = "";
        let district = "";
        let province = "";
        let country = "";
        let label = selectedLocation;

        result.address_components.forEach((component) => {
          const types = component.types;

          if (types.includes("locality")) {
            city = component.long_name;
          } else if (types.includes("administrative_area_level_2")) {
            district = component.long_name;
          } else if (types.includes("administrative_area_level_1")) {
            province = component.long_name;
          } else if (types.includes("country")) {
            country = component.long_name;
          }
        });

        if (lat && lng) {
          setLocationDetails({ city, district, province, country, latitude: lat, longitude: lng, label });
          setSearchCriteria((prev) => ({
            ...prev,
            location: {
              city,
              district,
              province,
              country,
              label,
              latitude: lat,
              longitude: lng,
            },
          }));
        }
      })
      .catch((err) => {
        console.error("Geocoding error:", err);
      });
  }, [params.destination, selectedLocation, url]);

  const pagination = extraDetails?.pagination;

  const searchParam = new URLSearchParams(location.search);
  const sortType = searchParam.get("sortType");

  let { country, destination, propertyType } = params;

  propertyType = propertyType?.replace(/s$/, "");

  const propertyTypeId = filterItems[2].items.find((item) => item.name === propertyType)?.value;

  const queryParams = processDestinationListingParams(location.search);

  const queryFilters = queryParams.filters || {};

  // this is important
  queryParams.bookingDates = {
    checkinDate: null,
    checkoutDate: null,
  };

  const selectedDestination = deriveDestinationFromLocation(searchCriteria.location);

  let isSearchChanged = isSearchValuesChanged(queryParams, {
    bookingDates: searchCriteria.bookingDates,
    guests: searchCriteria.guests,
  });

  isSearchChanged = isSearchChanged || destination !== selectedDestination;

  const paramFilters = {
    price: [],
    star: [queryFilters.minStarRating, queryFilters.maxStarRating],
    score: [],
    policy: [],
    propertyTypeIds: [propertyTypeId] || [],
    meals: [],
    deals: [],
  };

  async function fetchProperties() {
    const payload = {
      country,
      destination: destination.replace(" City", ""),
      pageSize: queryParams.pagination.limit,
      page: queryParams.pagination.page,
      sortBy: queryParams.sortOptions.by,
      sortOrder: queryParams.sortOptions.order,
      propertyId: queryParams.propertyDetails.propertyId,
      minStarRating: queryParams.filters.minStarRating,
      maxStarRating: queryParams.filters.maxStarRating,
    };

    if (propertyType) {
      payload.propertyType = paramFilters.propertyTypeIds[0];
    }

    getPropertiesByDestinationApi(payload)
      .then((res) => {
        setProperties(res?.data?.properties || []);
        setExtraDetails({
          pagination: res?.data?.pagination || {},
        });
      })
      .catch((err) => {
        toast.error(err.message);
        setProperties([]);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }

  // main part which fetches the data
  useEffect(() => {
    setIsLoading(true);

    const criteriaKeys = Object.keys(searchCriteria);
    const filteredParams = _.pick(queryParams, criteriaKeys);

    const location = searchCriteria.location;

    if (destination.includes("City")) {
      location.city = destination.replace(" City", "");
    } else if (destination.includes("District")) {
      location.district = destination.replace(" District", "");
    } else if (destination.includes("Province")) {
      location.province = destination.replace(" Province", "");
    } else {
      location.country = destination;
    }

    setSearchCriteria({
      ...filteredParams,
      location,
    });

    fetchProperties();
    return () => {
      setProperties([]);
      setSearchCriteria(initialSearchState);
      setExtraDetails(initialExtraDetailsState);
      setIsLoading(true);
    };
  }, [location.search, location.pathname]);

  const handlePageChange = (_, number) => {
    searchParams.set("page", number);
    searchParams.set("page-size", queryParams.pagination.limit);
    navigate(`?${searchParams.toString()}`);
  };

  const headingWording = generateHeadingWording(destination, propertyType, pagination?.totalCount);

  const notFoundWording = generateNotFoundWording(destination, propertyType ?? "", properties?.totalCount ?? 0);

  const getTitle = () => {
    if (destination && propertyType) {
      return `Explore ${propertyType} in ${destination}`;
    } else if (destination) {
      return `Explore accommodations in ${destination}`;
    }
    return "Explore Top Destinations";
  };

  const getDescription = () => {
    if (destination && propertyType) {
      return `Find the best ${propertyType} in ${destination}. Book your ideal stay today.`;
    } else if (destination) {
      return `Discover top accommodations in ${destination}. Find your ideal place to stay.`;
    }
    return "Discover a variety of travel hotspots and find your ideal getaway.";
  };

  const goToPropertyListingPage = () => {
    setIsTouched(true);

    if (errors.location || errors.bookingDates || errors.guests) {
      return;
    }

    const _searchCriteria = { ...searchCriteria };
    const { location, guests, bookingDates } = _searchCriteria;

    const noLocation = _.isEmpty(location?.country);

    if (noLocation) {
      setErrors({ ...errors, location: "Please select a location" });
      return;
    }

    if (guests.childCount > 0) {
      if (guests.childAges.length === 0) {
        setErrors({ ...errors, guests: "Please select the child age" });
        return;
      }

      const isValidAge = guests.childAges.every((age) => age >= 0 && age < 18);

      if (!isValidAge) {
        setErrors({ ...errors, guests: "Please select the child age" });
        return;
      }
    }

    let noOfNights = moment(bookingDates.checkoutDate).diff(moment(bookingDates.checkinDate), "days");

    if (noOfNights > 90) {
      setErrors({
        ...errors,
        bookingDates: "Select a date range up to 90 days",
      });
      return;
    }

    let checkin = moment(bookingDates.checkinDate);
    let checkout = moment(bookingDates.checkoutDate);

    // if (searchCriteria.propertyDetails.propertyId && (!checkin.isValid() || !checkout.isValid())) {
    //   setErrors({
    //     ...errors,
    //     bookingDates: "Please select dates",
    //   });
    //   return;
    // }

    if (!checkin.isValid()) {
      // In destination listing if user searches without dates then we should not show any error, just handle address only

      const url = generateDestinationListingUrl({
        searchCriteria: {
          location: { label: selectedDestination },
          propertyDetails: { propertyId: _searchCriteria.propertyDetails.propertyId },
        },
        // filters: {
        //   propertyType: propertyType ? `${propertyType}s` : undefined,
        // },
        // sortOptions: queryParams.sortOptions.type,
        pagination: {
          limit: 20,
          page: 1,
        },
      });

      navigate(url);
      return;
    }
    if (!checkout.isValid() || checkout.isBefore(checkin) || checkin.isSame(checkout, "day")) {
      checkout = checkin.clone().add(1, "day");

      _searchCriteria.bookingDates = {
        checkinDate: checkin.format("YYYY-MM-DD"),
        checkoutDate: checkout.format("YYYY-MM-DD"),
      };

      setSearchCriteria({
        ..._searchCriteria,
        location: {
          city: searchCriteria.location.city,
          country: searchCriteria.location.country,
          district: searchCriteria.location.district,
          province: searchCriteria.location.province,
          label: searchCriteria.location.label,
          latitude: searchCriteria.location.latitude,
          longitude: searchCriteria.location.longitude,
        },
      });
    }

    queryParams.pagination.page = 1;

    const url = generateListingUrl({
      params: queryParams,
      searchCriteria: {
        ..._searchCriteria,
        location: {
          city: searchCriteria.location.city,
          country: searchCriteria.location.country,
          district: searchCriteria.location.district,
          province: searchCriteria.location.province,
          label: searchCriteria.location.label,
          latitude: searchCriteria.location.latitude,
          longitude: searchCriteria.location.longitude,
        },
      },
    });

    navigate(url);
  };

  const hasProperties = properties?.length > 0;

  const handleSort = (value) => {
    const { pathname } = location;

    // Extract label and propertyType from the current pathname
    const pathParts = pathname.split("/").filter(Boolean);
    const label = decodeURIComponent(pathParts[2] || "");
    const params = new URLSearchParams(location.search);

    const star = params.get("star");

    const firstIndex = star?.split("-")[0];
    const lastIndex = star?.split("-")[1];

    // Prepare searchCriteria and filters
    const searchCriteria = {
      location: { label },
    };
    const filters = {
      propertyType,
      minStarRating: firstIndex ? parseInt(firstIndex, 10) : null,
      maxStarRating: lastIndex ? parseInt(lastIndex, 10) : null,
    };

    // Only add sortType if not "default"
    const sortType = value !== "default" ? value : null;

    // Build the new URL
    const newUrl = generateDestinationListingUrl({
      searchCriteria,
      filters,
      pagination: null, // You may want to preserve pagination here if needed
      sortType,
    });

    // Navigate to the generated URL
    navigate(newUrl);
  };

  return (
    <>
      <Helmet>
        <title>{getTitle()}</title>
        <meta name="description" content={getDescription()} />
      </Helmet>
      <div className=" container propert-listing-cards">
        <form
          className="mb-4"
          onSubmit={(e) => {
            e.preventDefault();
            goToPropertyListingPage();
          }}
        >
          <div className="prop-listing-search">
            <div>
              <LocationSearch searchCriteria={searchCriteria} setSearchCriteria={setSearchCriteria} setErrors={setErrors} />
              {isTouched && errors.location && <div className="error-msg-home">{errors.location || "Please select a location"}</div>}
            </div>

            <div className="top-filter">
              <CheckInCheckOutCalender searchCriteria={searchCriteria} setSearchCriteria={setSearchCriteria} setErrors={setErrors} />
              {/* {isTouched && errors.bookingDates && <div className="error-msg">{errors.bookingDates || "Please select dates"}</div>} */}
            </div>

            <div className="top-filter">
              <GuestInput searchCriteria={searchCriteria} setSearchCriteria={setSearchCriteria} setErrors={setErrors} />
              {isTouched && errors.guests && <div className="error-msg">{errors.guests || "Please select the child age"}</div>}
            </div>
            <Button className="listing-search-btn" type="submit">
              {isSearchChanged ? "Apply Changes" : "Search"}
            </Button>
          </div>
        </form>

        <div id="properties-list" className="d-listing-sort-wrapper">
          <div>
            {isLoading && (
              <div
                className="skeleton-loading mx-auto ms-sm-0 "
                style={{
                  height: "28.8px",
                  width: "382px",

                  maxWidth: "400px",
                  borderRadius: "4px",
                }}
              ></div>
            )}
            {!isLoading && !hasProperties && <h4 className="text-center text-sm-start m-0">{notFoundWording}</h4>}

            {!isLoading && hasProperties && <h4 className="text-center text-sm-start m-0">{headingWording}</h4>}
          </div>
          <div className="p-lising-des-sort">
            {/*Sorting Tab */}
            <div className="p-lising-sort-title">Sort by :</div>
            <DestinationsPropertiesSort handleSort={handleSort} selectedSort={sortType} />
          </div>
        </div>

        <div
          className="property-listing-wrapper"
          style={{
            marginTop: "16px",
          }}
        >
          {/* Filter Side Menu  */}
          <div className="p-listing-left d-none d-lg-block">
            {window.innerWidth >= 992 && (
              <GoogleMap
                params={params}
                filters={destinationFilterItems}
                paramFilters={paramFilters}
                searchCriteria={searchCriteria}
                setSearchCriteria={setSearchCriteria}
                setErrors={setErrors}
                locationDetails={locationDetails}
                handleSort={handleSort}
                sortType={sortType}
              />
            )}

            <FiltersDesktop
              searchCriteria={searchCriteria}
              params={params}
              filters={destinationFilterItems}
              paramFilters={paramFilters}
              expandable={false}
              isDestinations={true}
            />
          </div>

          {/* Property Listing and Sorting Tab */}
          <div className="p-listing-right">
            {/* Sorting and filter for small devices */}
            <div className="p-list-lg-filters d-lg-none filters">
              <PropertyFilterModal params={params} filters={destinationFilterItems} paramFilters={paramFilters} isDestinations={true} />

              <DestinationListingSortModal handleSort={handleSort} selectedSort={sortType} params={params} />
              {window.innerWidth < 992 && (
                <GoogleMap
                  params={params}
                  filters={destinationFilterItems}
                  paramFilters={paramFilters}
                  searchCriteria={searchCriteria}
                  setSearchCriteria={setSearchCriteria}
                  setErrors={setErrors}
                  locationDetails={locationDetails}
                  handleSort={handleSort}
                  sortType={sortType}
                />
              )}
            </div>
            {isLoading && <SkeletonCardsWrapper count={hasProperties ? properties?.length : 5} isDestinationListing={true} />}

            {!isLoading && !hasProperties && (
              <div className="afterload">
                <GlobalImage src={notAvailableImg} alt="no properties found" />
              </div>
            )}
            {!isLoading && hasProperties && properties.map((property) => <DestinationListingCard property={property} key={property.id} />)}
            {/* Pagination */}
            {!isLoading && hasProperties && (
              <div className="right_pagination">
                <Pagination
                  page={pagination.currentPage}
                  count={pagination.totalPages}
                  onChange={handlePageChange}
                  variant="outlined"
                  shape="rounded"
                  size="large"
                  siblingCount={window.innerWidth > 768 ? 1 : 0}
                />
              </div>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
}

export default DestinationListing;

function generateHeadingWording(destination = "", propertyType = "", totalProperties = 0) {
  destination = destination.replace(" City", "");

  let headingWording = destination + " : " + totalProperties + " ";

  if (propertyType) {
    headingWording += propertyType + `${totalProperties > 1 ? "s" : ""}`;
  } else {
    headingWording += "hotels and stays";
  }

  return headingWording;
}

function generateNotFoundWording(destination = "", propertyType = "", totalProperties = 0) {
  destination = destination.replace(" City", "");

  if (totalProperties > 0) return "";

  if (propertyType) {
    return `No ${propertyType}s found in ${destination}.`;
  }

  return `No properties found in ${destination}.`;
}

export function deriveDestinationFromLocation(location) {
  let destination = "Sri Lanka";

  if (location.city) {
    destination = location.city + " City";
  } else if (location.district) {
    destination = location.district + " District";
  } else if (location.province) {
    destination = location.province + " Province";
  } else if (location.country) {
    destination = location.country;
  }

  return destination;
}
